import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Table } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';

import {
  requestInformAnalysisQualityRawMaterial,
  requestDownload
} from 'api/sfms/report';
import Content from 'components/common-sfims/Content';
import Analysis from 'components/pages/sfms/report/analysis/quality/rawMaterial/Analysis';
import { downloadFile } from 'function/file';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import { useConfirmModalState } from 'global/confirmModalState';
import usePlantState from 'global/plantState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH, STYLE } from 'types/const';
import comma from 'utils/format/comma';
import format from 'utils/string/format';

/**
 * 리포트 > 품질 결과 분석 > 원료 - 조회
 */
const Inform = () => {
  /* router */
  const history = useHistory();
  const { state } = useLocation();
  const { type, reportYm, factoryCodeSeq, seq } = useParams();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();
  const {
    plant: { koreas: koreaPlants }
  } = usePlantState();

  /* state */
  // 분석 모달에 전달할 데이터
  const [analysisModalProps, setAnalysisModalProps] = useObjReducer({});
  // 목록
  const [datas, setDatas] = useState([]);
  // 다운로드 비활성화 여부
  const [isDisableDownload, setIsDisableDownload] = useState(false);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});
  // 공장
  const [pltNm, setPltNm] = useState('');

  /* watch */
  useEffect(() => {
    // 원료 품질 결과 분석 리포트 정보 조회 요청
    requestInform();
  }, []);
  useEffect(() => {
    setPltNm(
      koreaPlants.find(({ seq } = {}) => seq === Math.floor(factoryCodeSeq))
        ?.pltNm ?? ''
    );
  }, [koreaPlants]);

  /* method */
  // 리포트 다운로드 요청
  const requestDownloadReport = async () => {
    setIsDisableDownload(true);

    try {
      const { data, name } = await requestDownload(seq);

      downloadFile(data, name);
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableDownload(false);
    }
  };
  // 원료 품질 결과 분석 리포트 정보 조회 요청
  const requestInform = async () => {
    setDatas([]);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        code,
        message
      } = await requestInformAnalysisQualityRawMaterial({
        reportYm,
        factoryCodeSeq
      });

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setDatas(
        list.reduce(
          (result, { rwmtrCd, rwmtrNm, dataList = [] } = {}) => [
            ...result,
            ...dataList.map(value => ({
              ...value,
              rwmtrCd,
              rwmtrNm
            }))
          ],
          []
        )
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 분석 모달 표시
  const showAnalysis = value => {
    if (value.isData === 'Y') {
      setAnalysisModalProps({
        ...value,
        show: true
      });
    }
  };
  // 목록 화면 표시
  const showList = () => {
    history.push(format(ROUTER_PATH.sfms.report.list, { type }), state);
  };

  /* view */
  return (
    <>
      <Content>
        <Fragment key="headerButton">
          <Button
            disabled={isDisableDownload}
            size="sm"
            className="ms-2"
            onClick={requestDownloadReport}
          >
            {getText('downloadExcel')}
          </Button>
          <Button size="sm" className="ms-2" onClick={showList}>
            {getText('list')}
          </Button>
        </Fragment>
        <Fragment key="body">
          <Table bordered responsive className="fs--1 text-center mb-3">
            <thead className="bg-200 text-700 align-middle border-300">
              <tr>
                <th className="text-nowrap bg-soft-primary fs-0" colSpan={12}>
                  {format(getText('reportAnalysisQualityInform'), {
                    year: moment(reportYm).year(),
                    month: moment(reportYm).format('MM'),
                    pltNm,
                    type: getText('rawMaterial')
                  })}
                </th>
              </tr>
              <tr>
                <th className="text-nowrap" rowSpan={2}>
                  {getText('rwmtrCd')}
                </th>
                <th className="text-nowrap" rowSpan={2}>
                  {getText('rwmtrNm')}
                </th>
                <th className="text-nowrap" rowSpan={2}>
                  {getText('type')}
                </th>
                <th className="text-nowrap">{getText('moistureEn')}</th>
                <th className="text-nowrap">{getText('cproteinEn')}</th>
                <th className="text-nowrap">{getText('cfatEn')}</th>
                <th className="text-nowrap">{getText('cfiberEn')}</th>
                <th className="text-nowrap">{getText('ashEn')}</th>
                <th className="text-nowrap">{getText('caEn')}</th>
                <th className="text-nowrap">{getText('pEn')}</th>
                <th className="text-nowrap" rowSpan={2}>
                  {getText('isOver')}
                </th>
                <th className="text-nowrap" rowSpan={2}>
                  {getText('analysisType')}
                </th>
              </tr>
              <tr>
                <th className="text-nowrap">%</th>
                <th className="text-nowrap">%</th>
                <th className="text-nowrap">%</th>
                <th className="text-nowrap">%</th>
                <th className="text-nowrap">%</th>
                <th className="text-nowrap">%</th>
                <th className="text-nowrap">%</th>
              </tr>
            </thead>
            <tbody className="align-middle">
              {datas.length === 0 ? (
                <tr>
                  <td colSpan={12}>
                    {isLoading
                      ? getText('loading')
                      : message.code
                      ? getErrorMessage(message.code, message.error)
                      : getErrorMessage(RESPONSE_CODE.NotFoundData)}
                  </td>
                </tr>
              ) : (
                datas.map(
                  (
                    {
                      rwmtrCd,
                      rwmtrNm,
                      type,
                      isData,
                      moisture,
                      proteinIsOver,
                      protein,
                      cFat,
                      cFiber,
                      ash,
                      ca,
                      p,
                      isOver,
                      analysisType
                    } = {},
                    index
                  ) => (
                    <tr key={`datas${index}`}>
                      {index % 3 === 0 && (
                        <>
                          <td
                            className="bg-200 border-bottom border-300"
                            rowSpan={3}
                          >
                            {rwmtrCd || 'ㅡ'}
                          </td>
                          <td
                            className="bg-200 border-bottom border-300"
                            rowSpan={3}
                          >
                            {rwmtrNm || 'ㅡ'}
                          </td>
                        </>
                      )}
                      <td
                        className="bg-200 border-bottom border-300"
                        style={isData === 'Y' ? STYLE.link : {}}
                        onClick={() => {
                          showAnalysis({
                            type,
                            isData,
                            rwmtrCd,
                            analysisType
                          });
                        }}
                      >
                        {type || 'ㅡ'}
                      </td>
                      <td>{comma(moisture) || 'ㅡ'}</td>
                      <td
                        className={classNames({
                          'text-danger': proteinIsOver === 0
                        })}
                      >
                        {comma(protein) || 'ㅡ'}
                      </td>
                      <td>{comma(cFat) || 'ㅡ'}</td>
                      <td>{comma(cFiber) || 'ㅡ'}</td>
                      <td>{comma(ash) || 'ㅡ'}</td>
                      <td>{comma(ca) || 'ㅡ'}</td>
                      <td>{comma(p) || 'ㅡ'}</td>
                      <td>{isOver || 'ㅡ'}</td>
                      <td>{analysisType || 'ㅡ'}</td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </Table>
        </Fragment>
      </Content>
      <Analysis
        {...analysisModalProps}
        onHide={() => setAnalysisModalProps({ show: false })}
      />
    </>
  );
};

export default Inform;
